import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { Lightbulb, TrendingUp, RefreshCw, Target, Dumbbell, BookOpen, HelpCircle, FileText, X, Download, Printer, Copy, Check } from 'lucide-react';
import rmmbrLogo from '../assets/rmmbr.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function CoachingInsights({ clientId, onRefreshData, clientName }) {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [addingToReading, setAddingToReading] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    if (clientId) {
      fetchInsights();
    }
  }, [clientId]);

  const fetchInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/clients/${clientId}/insights`);
      setInsights(response.data);
      if (response.data.length > 0 && !selectedInsight) {
        setSelectedInsight(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
      setError('Failed to load coaching insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAddToReadingList = async (content) => {
    setAddingToReading(content.title);
    try {
      await axios.post(`${API_URL}/clients/${clientId}/readings`, {
        title: content.title,
        author: content.author,
        notes: content.description
      });

      toast.success(`Added "${content.title}" to reading list!`);

      // Refresh the client data if callback provided
      if (onRefreshData) {
        await onRefreshData();
      }
    } catch (error) {
      console.error('Error adding to reading list:', error);
      toast.error('Failed to add to reading list');
    } finally {
      setAddingToReading(null);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopyReport = async () => {
    if (!reportRef.current) return;

    const reportText = reportRef.current.innerText;
    try {
      await navigator.clipboard.writeText(reportText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy report');
    }
  };

  const handleDownloadPDF = () => {
    // For now, trigger print dialog which allows saving as PDF
    // In the future, could implement proper PDF generation with a library
    toast('Please use your browser\'s Print dialog (Ctrl/Cmd+P) and select "Save as PDF"');
    window.print();
  };

  if (loading) {
    return (
      <div className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-color)] p-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 mx-auto mb-4" style={{ borderColor: 'var(--border-color)', borderTopColor: 'var(--accent)' }}></div>
          <p className="text-[var(--text-tertiary)]">Loading coaching insights...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-color)] p-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400">
          {error}
        </div>
      </div>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-color)] p-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        <h3 className="text-2xl font-semibold text-[var(--text-primary)] mb-4">AI Coaching Insights</h3>
        <div className="text-center py-12">
          <Lightbulb className="w-16 h-16 mx-auto mb-4 text-[var(--text-tertiary)]" />
          <p className="text-[var(--text-tertiary)] text-base mb-2">No insights yet</p>
          <p className="text-[var(--text-tertiary)] text-sm">Insights are automatically generated when you upload session notes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-color)] p-6" style={{ boxShadow: 'var(--shadow-md)' }}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-2xl font-semibold text-[var(--text-primary)] flex items-center gap-2">
            <Lightbulb className="w-6 h-6" />
            <span>Session Insights</span>
          </h3>
          <p className="text-sm text-[var(--text-tertiary)] mt-1">AI-generated coaching recommendations and observations</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowReportModal(true)}
            disabled={!selectedInsight}
            className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-[var(--text-primary)] px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText className="w-4 h-4" />
            Generate Report
          </button>
          <button
            onClick={fetchInsights}
            className="bg-[var(--accent)]/20 hover:bg-[var(--accent)]/30 text-[var(--accent)] px-4 py-2 rounded-lg text-sm transition-all border border-[var(--accent)]/30"
          >
            Refresh
          </button>
        </div>
      </div>

      {selectedInsight ? (
        <div className="space-y-4">
          {/* Insight selector dropdown */}
          <div>
            <label className="block text-sm text-[var(--text-tertiary)] mb-2">Select Session Insight:</label>
            <select
              value={selectedInsight.id}
              onChange={(e) => {
                const insight = insights.find(i => i.id === parseInt(e.target.value));
                setSelectedInsight(insight);
              }}
              className="w-full bg-[var(--bg-tertiary)] text-[var(--text-primary)] px-4 py-2 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
              style={{ boxShadow: 'var(--shadow-sm)' }}
            >
              {insights.map((insight) => (
                <option key={insight.id} value={insight.id}>
                  {formatDate(insight.created_at)}
                </option>
              ))}
            </select>
          </div>

          {/* Insights display */}
          <div className="space-y-6">
            {/* Progress Summary */}
            {selectedInsight.progress_summary && (
              <div className="p-5 rounded-lg border border-[var(--accent)]/30" style={{ background: 'linear-gradient(to bottom right, var(--accent-20), var(--accent-15))', boxShadow: 'var(--shadow-sm)' }}>
                <h4 className="text-lg font-semibold text-[var(--accent)] mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  <span>Progress Summary</span>
                </h4>
                <p className="text-[var(--text-primary)] leading-relaxed">{selectedInsight.progress_summary}</p>
              </div>
            )}

            {/* Breakthroughs */}
            {selectedInsight.breakthroughs && (() => {
              try {
                const items = JSON.parse(selectedInsight.breakthroughs);
                if (items.length > 0) {
                  return (
                    <div className="bg-[var(--bg-tertiary)] p-5 rounded-lg border border-[var(--border-color)]" style={{ boxShadow: 'var(--shadow-sm)' }}>
                      <h4 className="text-lg font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                        <Lightbulb className="w-5 h-5" />
                        <span>Breakthroughs</span>
                      </h4>
                      <ul className="list-disc text-[var(--text-primary)] space-y-2 ml-4">
                        {items.map((item, idx) => (
                          <li key={idx} className="leading-relaxed pl-1">{item}</li>
                        ))}
                      </ul>
                    </div>
                  );
                }
              } catch (e) {
                return null;
              }
            })()}

            {/* Stuck Patterns */}
            {selectedInsight.stuck_patterns && (() => {
              try {
                const items = JSON.parse(selectedInsight.stuck_patterns);
                if (items.length > 0) {
                  return (
                    <div className="bg-[var(--bg-tertiary)] p-5 rounded-lg border border-[var(--border-color)]" style={{ boxShadow: 'var(--shadow-sm)' }}>
                      <h4 className="text-lg font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                        <RefreshCw className="w-5 h-5" />
                        <span>Stuck Patterns</span>
                      </h4>
                      <ul className="list-disc text-[var(--text-primary)] space-y-2 ml-4">
                        {items.map((item, idx) => (
                          <li key={idx} className="leading-relaxed pl-1">{item}</li>
                        ))}
                      </ul>
                    </div>
                  );
                }
              } catch (e) {
                return null;
              }
            })()}

            {/* Focus Areas */}
            {selectedInsight.focus_areas && (() => {
              try {
                const items = JSON.parse(selectedInsight.focus_areas);
                if (items.length > 0) {
                  return (
                    <div className="bg-[var(--bg-tertiary)] p-5 rounded-lg border border-[var(--border-color)]" style={{ boxShadow: 'var(--shadow-sm)' }}>
                      <h4 className="text-lg font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        <span>Focus Areas</span>
                      </h4>
                      <ul className="list-disc text-[var(--text-primary)] space-y-2 ml-4">
                        {items.map((item, idx) => (
                          <li key={idx} className="leading-relaxed pl-1">{item}</li>
                        ))}
                      </ul>
                    </div>
                  );
                }
              } catch (e) {
                return null;
              }
            })()}

            {/* Recommended Exercises */}
            {selectedInsight.recommended_exercises && (() => {
              try {
                const items = JSON.parse(selectedInsight.recommended_exercises);
                if (items.length > 0) {
                  return (
                    <div className="p-5 rounded-lg border border-[var(--success)]/30" style={{ background: 'var(--success-bg)', boxShadow: 'var(--shadow-sm)' }}>
                      <h4 className="text-lg font-semibold text-[var(--success)] mb-3 flex items-center gap-2">
                        <Dumbbell className="w-5 h-5" />
                        <span>Recommended Exercises</span>
                      </h4>
                      <ul className="list-disc text-[var(--text-primary)] space-y-2 ml-4">
                        {items.map((item, idx) => (
                          <li key={idx} className="leading-relaxed pl-1">{item}</li>
                        ))}
                      </ul>
                    </div>
                  );
                }
              } catch (e) {
                return null;
              }
            })()}

            {/* Questions for Next Session */}
            {selectedInsight.questions_for_next_session && (() => {
              try {
                const items = JSON.parse(selectedInsight.questions_for_next_session);
                if (items.length > 0) {
                  return (
                    <div className="bg-[var(--bg-tertiary)] p-5 rounded-lg border border-[var(--border-color)]" style={{ boxShadow: 'var(--shadow-sm)' }}>
                      <h4 className="text-lg font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                        <HelpCircle className="w-5 h-5" />
                        <span>Questions for Next Session</span>
                      </h4>
                      <ul className="list-disc text-[var(--text-primary)] space-y-2 ml-4">
                        {items.map((item, idx) => (
                          <li key={idx} className="leading-relaxed pl-1">{item}</li>
                        ))}
                      </ul>
                    </div>
                  );
                }
              } catch (e) {
                return null;
              }
            })()}

            {/* Recommended Content */}
            {selectedInsight.recommended_content && (() => {
              try {
                const items = JSON.parse(selectedInsight.recommended_content);
                if (items.length > 0) {
                  return (
                    <div className="p-5 rounded-lg border" style={{ background: 'linear-gradient(to bottom right, rgba(147, 51, 234, 0.15), rgba(59, 130, 246, 0.1))', borderColor: 'rgba(147, 51, 234, 0.3)', boxShadow: 'var(--shadow-sm)' }}>
                      <h4 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'rgb(216, 180, 254)' }}>
                        <BookOpen className="w-5 h-5" />
                        <span>Recommended Content</span>
                      </h4>
                      <div className="space-y-4">
                        {items.map((content, idx) => (
                          <div key={idx} className="bg-[var(--bg-tertiary)] p-4 rounded-lg border border-[var(--border-color)]" style={{ boxShadow: 'var(--shadow-sm)' }}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h5 className="text-[var(--text-primary)] font-semibold">{content.title}</h5>
                                  <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(147, 51, 234, 0.2)', color: 'rgb(216, 180, 254)' }}>
                                    {content.type}
                                  </span>
                                </div>
                                <p className="text-sm text-[var(--text-secondary)] mb-2">by {content.author}</p>
                                <p className="text-sm text-[var(--text-tertiary)]">{content.description}</p>
                                {content.url && (
                                  <a
                                    href={content.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs mt-2 inline-block hover:underline"
                                    style={{ color: 'rgb(216, 180, 254)' }}
                                  >
                                    View →
                                  </a>
                                )}
                              </div>
                              <button
                                onClick={() => handleAddToReadingList(content)}
                                disabled={addingToReading === content.title}
                                className="px-3 py-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-[var(--text-primary)] text-sm rounded-lg transition-all disabled:opacity-50 whitespace-nowrap flex-shrink-0"
                              >
                                {addingToReading === content.title ? 'Adding...' : '+ Add to Reading List'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
              } catch (e) {
                return null;
              }
            })()}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-[var(--text-tertiary)]">Select an insight to view details</p>
        </div>
      )}

      {/* Report Generation Modal */}
      {showReportModal && selectedInsight && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto print:p-0 print:bg-white">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8 print:shadow-none print:my-0">
            {/* Modal Header - Hidden when printing */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 print:hidden">
              <h3 className="text-2xl font-bold text-gray-900">RMMBR Session Insights</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyReport}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copy to clipboard"
                >
                  {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-gray-600" />}
                </button>
                <button
                  onClick={handlePrint}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Print"
                >
                  <Printer className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download as PDF"
                >
                  <Download className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Report Content */}
            <div ref={reportRef} className="p-8 print:p-12 space-y-8 bg-white text-gray-900">
              {/* Header with Logo */}
              <div className="text-center border-b-2 border-[var(--accent)] pb-6">
                <div className="flex items-center justify-center mb-4">
                  <img src={rmmbrLogo} alt="RMMBR" className="h-16 w-auto" />
                </div>
                <h1 className="text-4xl font-bold text-[#3a352f] mb-2">RMMBR Session Insights</h1>
                <p className="text-lg text-[#5a5248]">{clientName || 'Client'}</p>
                <p className="text-sm text-gray-500 mt-2">{formatDate(selectedInsight.created_at)}</p>
              </div>

              {/* Progress Summary */}
              {selectedInsight.progress_summary && (
                <div className="bg-gradient-to-br from-[#f5e6d3] to-[#ede0cf] p-6 rounded-lg border-l-4 border-[var(--accent)]">
                  <h2 className="text-2xl font-bold text-[#3a352f] mb-3 flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-[var(--accent)]" />
                    Progress Summary
                  </h2>
                  <p className="text-[#5a5248] leading-relaxed text-lg">{selectedInsight.progress_summary}</p>
                </div>
              )}

              {/* Breakthroughs */}
              {selectedInsight.breakthroughs && (() => {
                try {
                  const items = JSON.parse(selectedInsight.breakthroughs);
                  if (items.length > 0) {
                    return (
                      <div className="bg-gradient-to-br from-[#e8d4b8] to-[#f0e5d0] p-6 rounded-lg border-l-4 border-[#b8935f]">
                        <h2 className="text-2xl font-bold text-[#3a352f] mb-4 flex items-center gap-2">
                          <Lightbulb className="w-6 h-6 text-[#b8935f]" />
                          Breakthroughs & Wins
                        </h2>
                        <ul className="space-y-3">
                          {items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <span className="text-[var(--accent)] font-bold text-xl">•</span>
                              <span className="text-[#5a5248] leading-relaxed flex-1">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
              })()}

              {/* Stuck Patterns */}
              {selectedInsight.stuck_patterns && (() => {
                try {
                  const items = JSON.parse(selectedInsight.stuck_patterns);
                  if (items.length > 0) {
                    return (
                      <div className="bg-gradient-to-br from-[#d9c4a8] to-[#e8dcc8] p-6 rounded-lg border-l-4 border-[#a87f52]">
                        <h2 className="text-2xl font-bold text-[#3a352f] mb-4 flex items-center gap-2">
                          <RefreshCw className="w-6 h-6 text-[#a87f52]" />
                          Areas for Growth
                        </h2>
                        <ul className="space-y-3">
                          {items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <span className="text-[#a87f52] font-bold text-xl">•</span>
                              <span className="text-[#5a5248] leading-relaxed flex-1">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
              })()}

              {/* Focus Areas */}
              {selectedInsight.focus_areas && (() => {
                try {
                  const items = JSON.parse(selectedInsight.focus_areas);
                  if (items.length > 0) {
                    return (
                      <div className="bg-gradient-to-br from-[#cfb89a] to-[#e0d3be] p-6 rounded-lg border-l-4 border-[#8d6e47]">
                        <h2 className="text-2xl font-bold text-[#3a352f] mb-4 flex items-center gap-2">
                          <Target className="w-6 h-6 text-[#8d6e47]" />
                          Focus Areas
                        </h2>
                        <ul className="space-y-3">
                          {items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <span className="text-[#8d6e47] font-bold text-xl">•</span>
                              <span className="text-[#5a5248] leading-relaxed flex-1">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
              })()}

              {/* Recommended Exercises */}
              {selectedInsight.recommended_exercises && (() => {
                try {
                  const items = JSON.parse(selectedInsight.recommended_exercises);
                  if (items.length > 0) {
                    return (
                      <div className="bg-gradient-to-br from-[#c4a882] to-[#d9c9ad] p-6 rounded-lg border-l-4 border-[#9b7c54]">
                        <h2 className="text-2xl font-bold text-[#3a352f] mb-4 flex items-center gap-2">
                          <Dumbbell className="w-6 h-6 text-[#9b7c54]" />
                          Recommended Exercises
                        </h2>
                        <ul className="space-y-3">
                          {items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <span className="text-[#9b7c54] font-bold text-xl">•</span>
                              <span className="text-[#5a5248] leading-relaxed flex-1">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
              })()}

              {/* Questions for Next Session */}
              {selectedInsight.questions_for_next_session && (() => {
                try {
                  const items = JSON.parse(selectedInsight.questions_for_next_session);
                  if (items.length > 0) {
                    return (
                      <div className="bg-gradient-to-br from-[#bda178] to-[#d1bb98] p-6 rounded-lg border-l-4 border-[#8a6a3f]">
                        <h2 className="text-2xl font-bold text-[#3a352f] mb-4 flex items-center gap-2">
                          <HelpCircle className="w-6 h-6 text-[#8a6a3f]" />
                          Questions to Explore
                        </h2>
                        <ul className="space-y-3">
                          {items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <span className="text-[#8a6a3f] font-bold text-xl">•</span>
                              <span className="text-[#5a5248] leading-relaxed flex-1">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
              })()}

              {/* Recommended Content */}
              {selectedInsight.recommended_content && (() => {
                try {
                  const items = JSON.parse(selectedInsight.recommended_content);
                  if (items.length > 0) {
                    return (
                      <div className="bg-gradient-to-br from-[#b59970] to-[#c9b38f] p-6 rounded-lg border-l-4 border-[#7a5c36]">
                        <h2 className="text-2xl font-bold text-[#3a352f] mb-4 flex items-center gap-2">
                          <BookOpen className="w-6 h-6 text-[#7a5c36]" />
                          Recommended Resources
                        </h2>
                        <div className="space-y-4">
                          {items.map((content, idx) => (
                            <div key={idx} className="bg-white p-4 rounded-lg border border-[var(--accent)]/30 shadow-sm">
                              <div className="flex items-start gap-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-bold text-[#3a352f]">{content.title}</h3>
                                    <span className="text-xs bg-[var(--accent)]/20 text-[#7a5c36] px-2 py-1 rounded">
                                      {content.type}
                                    </span>
                                  </div>
                                  <p className="text-sm text-[#5a5248] mb-1">by {content.author}</p>
                                  <p className="text-sm text-[#6a6258]">{content.description}</p>
                                  {content.url && (
                                    <p className="text-xs text-[var(--accent)] mt-2 break-all">{content.url}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
              })()}

              {/* Footer */}
              <div className="text-center pt-8 border-t-2 border-[var(--accent)]">
                <div className="flex items-center justify-center mb-3">
                  <img src={rmmbrLogo} alt="RMMBR" className="h-8 w-auto opacity-60" />
                </div>
                <p className="text-sm text-[#5a5248] font-semibold">
                  The RMMBR Method
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  This report was generated using AI-powered coaching insights.
                </p>
                <p className="text-xs text-[var(--text-tertiary)] mt-1">
                  For questions or follow-up, please contact your coach.
                </p>
              </div>
            </div>

            {/* Modal Footer - Hidden when printing */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 print:hidden">
              <button
                onClick={() => setShowReportModal(false)}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
              >
                Close
              </button>
              <button
                onClick={handlePrint}
                className="px-6 py-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-[var(--text-primary)] rounded-lg transition-colors flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print / Save as PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoachingInsights;
